import React from "react";
import { useGesture } from "@use-gesture/react";
import { useSpring, useSpringRef, animated } from "@react-spring/web";
import { useTranslation } from "react-i18next";
import { CurrencyAmountDisplay } from "../CurrencyAmountDisplay";
import classNames from "classnames";

type SwipeButtonProps = {
    askingBidValue: number,
    handlePlaceBid: () => void
}

const SQUARE_BTN_SIZE_NUMBER = 40;

export default function SwipeButtonAnimatedCmp(props: SwipeButtonProps) {
    const {askingBidValue, handlePlaceBid} = props;

    const [swipeFinished, setSwipeFinished] = React.useState<boolean>(false);
    const bidBtnRef = React.useRef<HTMLButtonElement>(null);
    const bidBtnContentRef = React.useRef<HTMLDivElement>(null);
    const squareBtnRef = React.useRef<HTMLDivElement>(null);
    const { t, i18n } = useTranslation();

    const springSwipeBtnApiRef = useSpringRef();
    const springSwipeBtnAnimationStyles = useSpring({
        ref: springSwipeBtnApiRef,
        from: {
            x: 0,
            width: SQUARE_BTN_SIZE_NUMBER,
            height: SQUARE_BTN_SIZE_NUMBER,
            top: (0.5 * SQUARE_BTN_SIZE_NUMBER) / 2,
        }
    })

    const springBtnContentApiRef = useSpringRef();
    const springBtnContentAnimationStyles = useSpring({
        ref: springBtnContentApiRef,
        from: {
            x: 0,
        }
    })

    const bindSquareBtnDrag = useGesture({
            onDrag: ({ down, movement: [mx], intentional }) => {
                // threshold for sendBid
                const sendBidThreshold = mx > (0.75 * bidBtnRef.current.clientWidth - SQUARE_BTN_SIZE_NUMBER) && intentional;
                if (sendBidThreshold) {
                    setSwipeFinished(sendBidThreshold);
                    handlePlaceBid();
                }

                springSwipeBtnApiRef.start({
                    to: {
                        x: down ? mx : 0,
                        immediate: down
                    },
                    config: {
                        mass: 0.1,
                        tension: 500,
                        friction: 10.0,
                        precision: 0.01,
                        velocity: 0
                    }
                })

                springBtnContentApiRef.start({
                    to: {
                        x: down ? -(0.25 * SQUARE_BTN_SIZE_NUMBER) : 0,
                    }
                })
            },
            onPointerDown: ({ event }) => {
                if (event.currentTarget === squareBtnRef.current) {
                    squareBtnRef.current.classList.add('square-swipe-button__active');
                    document.body.classList.add('disable-text-select-on-touch');
                }
            },
            onPointerUp: ({ event }) => {
                if (event.currentTarget === squareBtnRef.current) {
                    squareBtnRef.current.classList.remove('square-swipe-button__active');
                    document.body.classList.remove('disable-text-select-on-touch');
                }
            }
        },
        {
            drag: {
                axis: 'x',
                bounds: bidBtnRef,
                preventScroll: true // https://github.com/pmndrs/use-gesture/issues/486
            }
        });

    return (
        <button type="button"
                ref={bidBtnRef}
                className='w-100 py-3 pe-3 h4 btn-lg btn-bid styled-swipe-button'
                style={{
                    paddingLeft: `calc(${SQUARE_BTN_SIZE_NUMBER}px + 0.25 * ${SQUARE_BTN_SIZE_NUMBER}px)`,
                }}
                disabled={swipeFinished}
        >
            <animated.div
                ref={squareBtnRef}
                className={classNames(swipeFinished ? 'd-none' : 'square-swipe-button')}
                style={{ ...springSwipeBtnAnimationStyles }}
                {...bindSquareBtnDrag()}
            >
                <i className="fa-regular fa-angles-right"></i>
            </animated.div>

            <animated.div
                ref={bidBtnContentRef}
                style={{...springBtnContentAnimationStyles}}
            >
                {
                    swipeFinished ?
                        `${t('biddingArea.bidding')}` :
                        `${t('biddingArea.swipeToBid')}`
                }
                <CurrencyAmountDisplay valueCents={askingBidValue}/>
            </animated.div>
        </button>
    );
};
